import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../../../utils/supabase";
import useUserStore from "../../../stores/user";
import { toast } from "react-toastify";
import { getStandardTerminology } from "../../../utils/standardsUtils";
import { useT } from "../../../stores/uiLang";
import { TD_T } from "../../../i18n/teacherDashboard";

interface Student {
  id: string;
  fullname: string;
  firstname: string;
  lastname: string;
}

interface Assignment {
  id: string;
  assessment_name: string;
  type: string;
  standard: string;
  assessment_id: string;
}

interface Class {
  id: string;
  name: string;
}

interface GradebookCell {
  studentId: string;
  assignmentId: string;
  submissionId?: string;
  status: string;
  grade?: string;
  gradePercentage?: number;
}

export default function Gradebook() {
  const t = useT(TD_T);
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [selectedClass, setSelectedClass] = useState<string>("all");
  const [selectedStandard, setSelectedStandard] = useState<string>("all");
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [availableStandards, setAvailableStandards] = useState<string[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<Assignment[]>(
    [],
  );
  const stateName = useUserStore((state) => state.stateName);
  const terminology = getStandardTerminology(stateName || "Oklahoma");

  const [gradebookData, setGradebookData] = useState<
    Map<string, GradebookCell>
  >(new Map());
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalStudentsCount, setTotalStudentsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNumericPercentage, setShowNumericPercentage] = useState(false);
  const studentsPerPage = 10;

  // Fetch available classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!user?.id) return;

      try {
        const { data: classes, error } = await supabase
          .from("classes")
          .select("id, name")
          .eq("user_id", user.id)
          .order("name");

        if (error) {
          console.error("Error fetching classes:", error);
          toast.error("Failed to fetch classes");
          return;
        }

        setAvailableClasses(classes || []);
        // Keep "all" as default - don't auto-select first class
      } catch (error) {
        console.error("Error fetching classes:", error);
        toast.error("Failed to fetch classes");
      }
    };

    fetchClasses();
  }, [user?.id]);

  // Fetch students and assignments when class changes
  useEffect(() => {
    if (selectedClass && user?.id) {
      fetchGradebookData(currentPage, searchQuery);
    } else {
      setStudents([]);
      setAssignments([]);
      setFilteredAssignments([]);
      setGradebookData(new Map());
      setAvailableStandards([]);
    }
    // Reset to first page when class changes
    setCurrentPage(1);
  }, [selectedClass, user?.id]);

  // Reset to first page when search changes
  useEffect(() => {
    if (selectedClass && user?.id) {
      setCurrentPage(1);
      fetchGradebookData(1, searchQuery);
    }
  }, [searchQuery]);

  // Fetch data when page changes
  useEffect(() => {
    if (selectedClass && user?.id && currentPage > 0) {
      fetchGradebookData(currentPage, searchQuery);
    }
  }, [currentPage]);

  // Filter assignments by standard when standard filter changes
  useEffect(() => {
    if (selectedStandard === "all") {
      setFilteredAssignments(assignments);
    } else {
      const filtered = assignments.filter(
        (a) => a.standard === selectedStandard,
      );
      setFilteredAssignments(filtered);
    }
  }, [selectedStandard, assignments]);

  const fetchGradebookData = async (page: number = 1, search: string = "") => {
    if (!user?.id || !selectedClass) return;

    setLoading(true);
    try {
      // Fetch students - either from selected class or all classes
      let classStudentsQuery = supabase
        .from("class_students")
        .select("user_id");

      const isAllClasses = selectedClass === "all";

      if (isAllClasses) {
        // Get all classes for this teacher first
        const { data: teacherClasses } = await supabase
          .from("classes")
          .select("id")
          .eq("user_id", user.id);

        if (teacherClasses && teacherClasses.length > 0) {
          const classIds = teacherClasses.map((c) => c.id);
          classStudentsQuery = classStudentsQuery.in("class_id", classIds);
        } else {
          // No classes, return empty
          setStudents([]);
          setAssignments([]);
          setFilteredAssignments([]);
          setGradebookData(new Map());
          setAvailableStandards([]);
          setLoading(false);
          return;
        }
      } else {
        classStudentsQuery = classStudentsQuery.eq("class_id", selectedClass);
      }

      const { data: classStudents, error: classError } =
        await classStudentsQuery;

      if (classError) {
        console.error("Error fetching class students:", classError);
        toast.error("Failed to fetch students");
        setLoading(false);
        return;
      }

      if (!classStudents || classStudents.length === 0) {
        setStudents([]);
        setAssignments([]);
        setGradebookData(new Map());
        setLoading(false);
        return;
      }

      // Get unique student IDs (in case a student is in multiple classes)
      const uniqueStudentIds = Array.from(
        new Set(classStudents.map((cs) => cs.user_id)),
      );

      // Apply search filter if provided
      let filteredStudentIds = uniqueStudentIds;
      if (search.trim()) {
        // First fetch all profiles to filter by name, then paginate
        const { data: allProfiles } = await supabase
          .from("profiles")
          .select("id, firstname, lastname")
          .in("id", uniqueStudentIds);

        if (allProfiles) {
          const searchLower = search.toLowerCase().trim();
          const matchingProfiles = allProfiles.filter((profile: any) => {
            const fullname = `${profile.firstname || ""} ${
              profile.lastname || ""
            }`.toLowerCase();
            return fullname.includes(searchLower);
          });
          filteredStudentIds = matchingProfiles.map((p: any) => p.id);
        }
      }

      // Update total count based on filtered results
      setTotalStudentsCount(filteredStudentIds.length);

      if (filteredStudentIds.length === 0) {
        setStudents([]);
        setAssignments([]);
        setFilteredAssignments([]);
        setGradebookData(new Map());
        setLoading(false);
        return;
      }

      // Calculate pagination for students - only fetch the current page
      const startIndex = (page - 1) * studentsPerPage;
      const endIndex = startIndex + studentsPerPage;
      const paginatedStudentIds = filteredStudentIds.slice(
        startIndex,
        endIndex,
      );

      // Fetch profiles for paginated students only
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, firstname, lastname")
        .in("id", paginatedStudentIds);

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        toast.error("Failed to fetch student profiles");
        setLoading(false);
        return;
      }

      const studentList: Student[] =
        profiles
          ?.map((profile: any) => ({
            id: profile.id,
            firstname: profile.firstname || "",
            lastname: profile.lastname || "",
            fullname: `${profile.firstname || ""} ${
              profile.lastname || ""
            }`.trim(),
          }))
          .filter((s: Student) => s.fullname) || [];

      setStudents(studentList);

      if (studentList.length === 0) {
        setAssignments([]);
        setGradebookData(new Map());
        setLoading(false);
        return;
      }

      // Use the student IDs from the profiles we fetched (these are the paginated students)
      const validStudentIds = studentList.map((s) => s.id);

      // Get all class IDs for filtering assignments
      let classIdsForFiltering: string[] = [];
      if (isAllClasses) {
        const { data: teacherClasses } = await supabase
          .from("classes")
          .select("id")
          .eq("user_id", user.id);
        classIdsForFiltering = teacherClasses?.map((c) => c.id) || [];
      } else {
        classIdsForFiltering = [selectedClass];
      }

      // Fetch all assignments for this teacher first
      const { data: allTeacherAssignments, error: allAssignmentsError } =
        await supabase
          .from("assignments")
          .select(
            "id, assessment_name, type, standard, assessment_id, assign_to, assigned_class, selected_students",
          )
          .eq("user_id", user.id)
          .order("standard", { ascending: true })
          .order("assessment_name", { ascending: true });

      if (allAssignmentsError) {
        console.error("Error fetching assignments:", allAssignmentsError);
        toast.error("Failed to fetch assignments");
        setLoading(false);
        return;
      }

      // Filter assignments to only those assigned to the selected class(es)
      const teacherAssignments = (allTeacherAssignments || []).filter(
        (assignment) => {
          // If assigned to class, check if any of the selected classes are in the assigned_class array
          if (assignment.assign_to === "class" && assignment.assigned_class) {
            const assignedClasses = Array.isArray(assignment.assigned_class)
              ? assignment.assigned_class
              : [];
            return assignedClasses.some((cls: any) =>
              classIdsForFiltering.includes(cls.id),
            );
          }
          // If assigned to individuals, check if any students in the selected class(es) are selected
          if (
            assignment.assign_to === "individuals" &&
            assignment.selected_students
          ) {
            const selectedStudentIds = Array.isArray(
              assignment.selected_students,
            )
              ? assignment.selected_students
              : [];
            return validStudentIds.some((id) =>
              selectedStudentIds.includes(id),
            );
          }
          return false;
        },
      );

      // Get assignment IDs for fetching student_assignments
      const assignmentIdsForClass = new Set(
        teacherAssignments.map((a) => a.id),
      );

      if (assignmentIdsForClass.size === 0) {
        setAssignments([]);
        setFilteredAssignments([]);
        setAvailableStandards([]);
        setGradebookData(new Map());
        setLoading(false);
        return;
      }

      // Now get student assignments for these assignments and students
      const { data: studentAssignmentsForClass, error: classAssignmentsError } =
        await supabase
          .from("student_assignments")
          .select("assignment_id")
          .in("student_id", validStudentIds)
          .in("assignment_id", Array.from(assignmentIdsForClass));

      if (classAssignmentsError) {
        console.error(
          "Error fetching student assignments:",
          classAssignmentsError,
        );
      }

      if (allAssignmentsError) {
        console.error("Error fetching assignments:", allAssignmentsError);
        toast.error("Failed to fetch assignments");
        setLoading(false);
        return;
      }

      // Convert to Assignment[] format
      const assignmentList: Assignment[] = teacherAssignments.map((a) => ({
        id: a.id,
        assessment_name: a.assessment_name,
        type: a.type,
        standard: a.standard,
        assessment_id: a.assessment_id,
      }));

      setAssignments(assignmentList);

      // Extract unique standards for filter
      const standards = new Set<string>();
      for (const a of assignmentList) {
        if (a.standard) {
          standards.add(a.standard);
        }
      }
      const sortedStandards = Array.from(standards).sort((a, b) => {
        // Sort by standard number if it's in format "Standard X" or "X"
        const numA = Number.parseInt(a.replace(/\D/g, ""), 10) || 0;
        const numB = Number.parseInt(b.replace(/\D/g, ""), 10) || 0;
        return numA - numB;
      });
      setAvailableStandards(sortedStandards);

      // Set initial filtered assignments
      if (selectedStandard === "all") {
        setFilteredAssignments(assignmentList);
      } else {
        setFilteredAssignments(
          assignmentList.filter((a) => a.standard === selectedStandard),
        );
      }

      // Fetch student assignments for these students and assignments
      const { data: studentAssignments, error: studentAssignmentsError } =
        await supabase
          .from("student_assignments")
          .select(
            "id, student_id, assignment_id, status, grade, grade_percentage, submitted_at",
          )
          .in("student_id", validStudentIds)
          .in(
            "assignment_id",
            assignmentList.map((a) => a.id),
          );

      if (studentAssignmentsError) {
        console.error(
          "Error fetching student assignments:",
          studentAssignmentsError,
        );
        toast.error("Failed to fetch student assignments");
        setLoading(false);
        return;
      }

      const dataMap = new Map<string, GradebookCell>();

      if (studentAssignments) {
        for (const sa of studentAssignments) {
          const key = `${sa.student_id}-${sa.assignment_id}`;
          dataMap.set(key, {
            studentId: sa.student_id,
            assignmentId: sa.assignment_id,
            submissionId: sa.id,
            status: sa.status,
            grade: sa.grade,
            gradePercentage: sa.grade_percentage,
          });
        }
      }

      setGradebookData(dataMap);
    } catch (error) {
      console.error("Error fetching gradebook data:", error);
      toast.error("Failed to load gradebook data");
    } finally {
      setLoading(false);
    }
  };

  const getCellStatus = (
    cell: GradebookCell | undefined,
  ): "complete" | "in_progress" | "needs_review" | "not_started" => {
    if (!cell) return "not_started";

    if (cell.status === "completed") {
      if (cell.gradePercentage !== undefined && cell.gradePercentage < 70) {
        return "needs_review";
      }
      return "complete";
    }

    if (cell.status === "in_progress") {
      return "in_progress";
    }

    return "not_started";
  };

  const getCellColor = (
    status: "complete" | "in_progress" | "needs_review" | "not_started",
  ): string => {
    switch (status) {
      case "complete":
        return "bg-green-100 hover:bg-green-200 ";
      case "in_progress":
        return "bg-yellow-100 hover:bg-yellow-200 ";
      case "needs_review":
        return "bg-red-100 hover:bg-red-200 ";
      case "not_started":
        return "bg-gray-50 hover:bg-gray-100 border-gray-200";
      default:
        return "bg-gray-50 hover:bg-gray-100 border-gray-200";
    }
  };

  const getCellText = (
    cell: GradebookCell | undefined,
    status: string,
  ): string => {
    if (!cell) return "-";

    if (status === "complete" || status === "needs_review") {
      if (showNumericPercentage) {
        // Show numeric percentage
        if (cell.gradePercentage !== undefined) {
          return `${cell.gradePercentage}%`;
        }
        if (cell.grade) {
          return cell.grade;
        }
        return "✓";
      } else {
        // Show grade by type (current grade)
        if (cell.grade) {
          return cell.grade;
        }
        if (cell.gradePercentage !== undefined) {
          return `${cell.gradePercentage}%`;
        }
        return "✓";
      }
    }

    if (status === "in_progress") {
      return "In Progress";
    }

    return "-";
  };

  const getCellTitle = (
    cell: GradebookCell | undefined,
    status: string,
  ): string => {
    if (cell?.submissionId) {
      return "Click to review submission";
    }

    switch (status) {
      case "not_started":
        return "Not started";
      case "in_progress":
        return "In progress";
      case "needs_review":
        return "Needs review";
      case "complete":
        return "Complete";
      default:
        return "";
    }
  };

  const handleCellClick = (cell: GradebookCell | undefined) => {
    if (!cell?.submissionId) {
      return;
    }

    navigate(`/submission-review/${cell.submissionId}`);
  };

  const groupedAssignments = filteredAssignments.reduce(
    (acc, assignment) => {
      const key = assignment.standard || "Other";
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(assignment);
      return acc;
    },
    {} as Record<string, Assignment[]>,
  );

  return (
    <>
      <style>{`
        *, ::before, ::after {
          box-sizing: border-box;
          border-width: 0;
          border-style: solid;
          border-color: #e5e7eb !important;
        }
      `}</style>
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t.gradebook}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {t.gradebookSub}
              </p>
            </div>
            <div className="flex justify-between items-center flex-wrap gap-4 w-full">
              <div className="flex items-center space-x-2">
                <label
                  htmlFor="student-search"
                  className="text-sm font-medium text-gray-700"
                >
                  {t.search}
                </label>
                <input
                  id="student-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.searchByName}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 min-w-[200px]"
                />
              </div>
              <div className="flex items-center justify-end gap-10">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="show-numeric-percentage"
                    checked={showNumericPercentage}
                    onChange={(e) => setShowNumericPercentage(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="show-numeric-percentage"
                    className="text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    {t.showPercent}
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor="class-filter"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t.classLabel}
                  </label>
                  <select
                    id="class-filter"
                    value={selectedClass}
                    onChange={(e) => {
                      setSelectedClass(e.target.value);
                      setSelectedStandard("all");
                      setSearchQuery("");
                    }}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="all">{t.allClasses}</option>
                    {availableClasses.map((cls) => (
                      <option key={cls.id} value={cls.id}>
                        {cls.name}
                      </option>
                    ))}
                  </select>
                </div>

                {availableStandards.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <label
                      htmlFor="standard-filter"
                      className="text-sm font-medium text-gray-700"
                    >
                      {terminology}:
                    </label>
                    <select
                      id="standard-filter"
                      value={selectedStandard}
                      onChange={(e) => setSelectedStandard(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 min-w-[150px]"
                    >
                      <option value="all">{t.allRecords}</option>
                      {availableStandards.map((standard) => (
                        <option key={standard} value={standard}>
                          {standard}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="p-6 text-center text-gray-500">
                {t.loadingGradebook}
              </div>
            );
          }
          if (students.length === 0) {
            const emptyMessage =
              selectedClass === "all"
                ? "Please select a class to view the gradebook"
                : "No students found in this class";
            return (
              <div className="p-6 text-center text-gray-500">
                {emptyMessage}
              </div>
            );
          }
          if (assignments.length === 0) {
            return (
              <div className="p-6 text-center text-gray-500">
                {t.noAssignmentsGb}
              </div>
            );
          }
          if (filteredAssignments.length === 0) {
            return (
              <div className="p-6 text-center text-gray-500">
                {t.noAssignmentsFilter}
              </div>
            );
          }
          return (
            <div className="overflow-x-auto overflow-y-visible">
              <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-64">
                        {t.student}
                      </th>
                      {Object.entries(groupedAssignments).map(
                        ([groupKey, groupAssignments], index) => {
                          const assignmentCount = groupAssignments.length;
                          const isNotFirst = index > 0;
                          return (
                            <th
                              key={groupKey}
                              className={`px-4 py-3 text-center min-w-52 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100 border-b border-gray-200 ${
                                isNotFirst ? "border-l-2 border-l-gray-400" : ""
                              }`}
                              colSpan={assignmentCount}
                            >
                              <div className="flex flex-col items-center justify-center">
                                <span className="font-semibold text-gray-700 text-center">
                                  {groupKey}
                                </span>
                                {assignmentCount > 1 && (
                                  <span className="text-xs text-gray-500 mt-1">
                                    {assignmentCount} assignments
                                  </span>
                                )}
                              </div>
                            </th>
                          );
                        },
                      )}
                    </tr>
                    <tr>
                      <th className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 w-48"></th>
                      {filteredAssignments.map((assignment, index) => {
                        const standard = assignment.standard || "Other";
                        const prevAssignment =
                          index > 0 ? filteredAssignments[index - 1] : null;
                        const prevStandard =
                          prevAssignment?.standard || "Other";
                        const isNewStandard = standard !== prevStandard;

                        return (
                          <th
                            key={assignment.id}
                            className={`px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider ${
                              isNewStandard
                                ? "border-l-2 border-l-gray-400"
                                : ""
                            }`}
                            title={assignment.assessment_name}
                          >
                            <div className="flex flex-col items-center justify-center min-w-max">
                              <span
                                className="truncate w-full text-center"
                                title={assignment.assessment_name}
                              >
                                {assignment.assessment_name.length > 15
                                  ? `${assignment.assessment_name.substring(
                                      0,
                                      15,
                                    )}...`
                                  : assignment.assessment_name}
                              </span>
                              <span className="text-xs text-gray-400 mt-1">
                                {assignment.type}
                              </span>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students.map((student) => (
                      <tr key={student.id} className="hover:bg-gray-50">
                        <td className="sticky left-0 z-20 bg-white px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-48">
                          <div className="flex items-center justify-start">
                            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center mr-2">
                              <span className="text-sm font-medium text-indigo-800">
                                {student.firstname[0]?.toUpperCase()}
                                {student.lastname[0]?.toUpperCase()}
                              </span>
                            </div>
                            <span className="text-center">
                              {student.fullname}
                            </span>
                          </div>
                        </td>
                        {filteredAssignments.map((assignment, index) => {
                          const cellKey = `${student.id}-${assignment.id}`;
                          const cell = gradebookData.get(cellKey);
                          const status = getCellStatus(cell);
                          const cellColor = getCellColor(status);
                          const cellText = getCellText(cell, status);
                          const isClickable = cell?.submissionId;

                          const cellTitle = getCellTitle(cell, status);

                          const standard = assignment.standard || "Other";
                          const prevAssignment =
                            index > 0 ? filteredAssignments[index - 1] : null;
                          const prevStandard =
                            prevAssignment?.standard || "Other";
                          const isNewStandard = standard !== prevStandard;

                          return (
                            <td
                              key={assignment.id}
                              className={`px-2 py-2 text-center text-sm border ${
                                isClickable
                                  ? "cursor-pointer"
                                  : "cursor-default"
                              } ${cellColor} transition-colors ${
                                isNewStandard
                                  ? "border-l-2 border-l-gray-400"
                                  : ""
                              }`}
                              onClick={() => handleCellClick(cell)}
                              title={cellTitle}
                            >
                              {cellText}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {totalStudentsCount > studentsPerPage && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing{" "}
                {Math.min(
                  (currentPage - 1) * studentsPerPage + 1,
                  totalStudentsCount,
                )}
                -{Math.min(currentPage * studentsPerPage, totalStudentsCount)}{" "}
                of {totalStudentsCount} students
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t.previous}
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of{" "}
                  {Math.ceil(totalStudentsCount / studentsPerPage)}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(
                        Math.ceil(totalStudentsCount / studentsPerPage),
                        prev + 1,
                      ),
                    )
                  }
                  disabled={
                    currentPage >=
                      Math.ceil(totalStudentsCount / studentsPerPage) || loading
                  }
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t.next}
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-start space-x-6 text-xs">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
              <span className="text-gray-700">{t.legendComplete}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
              <span className="text-gray-700">{t.legendInProgress}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
              <span className="text-gray-700">{t.legendNeedsReview}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-50 border border-gray-200 rounded"></div>
              <span className="text-gray-700">{t.legendNotStarted}</span>
            </div>
          </div>
        </div>
      </div>

    </>
  );
}
